import { z } from 'zod/v4';
import { Interval, } from '../../economy/payment-plans/index.js';
import { majorToCents } from '../../util/money.js';
import { ToolCategory, ToolName, ToolPolicy, ToolWeight } from '../types.js';
// Flat object, not object().and(discriminatedUnion(...)): Gemini requires a single
// object schema for function parameters and Vertex rejects the intersection's allOf/anyOf.
const inputSchema = z
    .object({
    startDate: z.iso
        .date()
        .describe('When the plan begins. The first installment (or amountUpfront, if set) is due on or near this date. Must not be in the past.'),
    interval: z
        .enum(Interval)
        .describe('How frequently installments recur. WEEK / TWO-WEEKS / MONTH / TWO-MONTHS / QUARTERLY / YEARLY / LASTBANKDAYMONTH.'),
    amountUpfront: z
        .number()
        .positive()
        .nullish()
        .describe('Optional initial down payment, paid before the regular installments begin. Often used to bring a case current. Amount in the case currency.'),
    dateUpfront: z.iso
        .date()
        .nullish()
        .describe('When the upfront amount is due. Defaults to startDate when omitted. Has no effect unless amountUpfront is also set.'),
    method: z
        .enum(['duration', 'amount'])
        .describe('\'duration\' generates a fixed number of installments and lets the system compute the per-installment amount from the outstanding balance — use for "X monthly installments" and set `duration`. \'amount\' fixes the recurring installment amount and runs as long as needed to settle the balance — use for "<amount> per month" and set `amount`.'),
    duration: z
        .number()
        .int()
        .min(1)
        .max(60)
        .optional()
        .describe('Number of installments to generate. REQUIRED when method is "duration"; must be omitted when method is "amount". Per-installment amount = (balance − amountUpfront) / duration, recalculated by the system.'),
    amount: z
        .number()
        .positive()
        .optional()
        .describe('Recurring per-installment amount in the case currency. REQUIRED when method is "amount"; must be omitted when method is "duration". The plan continues at this amount until the balance is exhausted.'),
    scheduledAmountChanges: z
        .array(z.object({
        amount: z
            .number()
            .positive()
            .describe('New installment amount in the case currency, taking effect on `date`.'),
        date: z.iso.date(),
    }))
        .nullish()
        .describe('Optional step-changes to the installment amount at specific dates (e.g. debtor expects a raise on a known date). Only valid when method is "amount".'),
    reasoning: z
        .string()
        .max(500)
        .optional()
        .describe('Why you are proposing this plan. Short rationale visible to the caseworker reviewing the proposal.'),
})
    .superRefine((input, ctx) => {
    if (input.method === 'duration') {
        if (input.duration == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['duration'],
                message: 'duration is required when method is "duration".',
            });
        }
        if (input.amount != null) {
            ctx.addIssue({
                code: 'custom',
                path: ['amount'],
                message: 'amount must be omitted when method is "duration".',
            });
        }
        if (input.scheduledAmountChanges != null &&
            input.scheduledAmountChanges.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['scheduledAmountChanges'],
                message: 'scheduledAmountChanges is only valid when method is "amount".',
            });
        }
    }
    else {
        if (input.amount == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['amount'],
                message: 'amount is required when method is "amount".',
            });
        }
        if (input.duration != null) {
            ctx.addIssue({
                code: 'custom',
                path: ['duration'],
                message: 'duration must be omitted when method is "amount".',
            });
        }
    }
});
const outputSchema = z.object({
    paymentPlanId: z.uuid(),
});
// Switching method must also clear the fields belonging to the old method.
// Explicit `undefined` values survive the shallow patch merge long enough for
// inputSchema to strip them from the validated result.
const editablePaymentPlanFields = {
    startDate: z.iso.date().optional(),
    interval: z.enum(Interval).optional(),
    amountUpfront: z.number().positive().nullish(),
    dateUpfront: z.iso.date().nullish(),
};
const editSchema = z
    .discriminatedUnion('method', [
    z.object({
        ...editablePaymentPlanFields,
        method: z.literal('duration'),
        duration: z.number().int().min(1).max(60),
    }),
    z.object({
        ...editablePaymentPlanFields,
        method: z.literal('amount'),
        amount: z.number().positive(),
        scheduledAmountChanges: z
            .array(z.object({
            amount: z.number().positive(),
            date: z.iso.date(),
        }))
            .nullish(),
    }),
])
    .transform((edits) => {
    if (edits.method === 'duration') {
        return {
            ...edits,
            amount: undefined,
            scheduledAmountChanges: undefined,
        };
    }
    return { ...edits, duration: undefined };
});
export const toPaymentPlanConfig = (input, feeStructure) => {
    const amountUpfront = input.amountUpfront != null ? majorToCents(input.amountUpfront) : null;
    // dateUpfront must default to startDate: gen() builds the upfront entry from
    // moment(config.dateUpfront), and moment(null) is an invalid date.
    const base = {
        startDate: input.startDate,
        interval: input.interval,
        amountUpfront,
        dateUpfront: input.dateUpfront ?? (amountUpfront != null ? input.startDate : null),
        feeStructure,
    };
    // Not dead code: direct callers can pass a stored proposedInput cast that bypassed superRefine.
    if (input.method === 'duration') {
        if (input.duration == null) {
            throw new Error('duration is required when method is "duration".');
        }
        return { ...base, method: 'duration', duration: input.duration };
    }
    if (input.amount == null) {
        throw new Error('amount is required when method is "amount".');
    }
    return {
        ...base,
        method: 'amount',
        amount: majorToCents(input.amount),
        scheduledAmountChanges: input.scheduledAmountChanges?.map((c) => ({
            amount: majorToCents(c.amount),
            date: c.date,
        })) ?? null,
    };
};
export const createPaymentPlanTool = {
    name: ToolName.CreatePaymentPlan,
    category: ToolCategory.Vouchers,
    weight: ToolWeight.Heavy,
    version: 2,
    defaultPolicy: ToolPolicy.RequireApproval,
    llmDescription: "Propose a new payment plan for this case. HEAVY: calling this ends the run; a caseworker will review and may edit before approving. Before calling, inspect get_current_payment_plan and list_pending_proposals. Never call when a CREATED (not yet activated) or ACTIVE plan exists. When an open CREATE_PAYMENT_PLAN proposal has different terms that the debtor has since replaced with new concrete terms, proposing the new terms automatically supersedes the old proposal; do not replace an equivalent proposal with a duplicate. This tool cannot edit or replace an existing plan and cannot skip, postpone, or reschedule an installment; escalate those requests to a human. Use only when the debtor has specifically asked for a concrete amount or concrete duration/installment count and the case constraints allow it. When the debtor gives a numeric range, use its highest value without clarification unless TENANT GUIDANCE or the TASK says otherwise. Do not invent plan numbers from tenant-preferred shapes, the outstanding balance, or a general wish to pay in installments; in that situation, propose sending the debtor the payment-plan link instead, unless the debtor says the self-service options do not work or they have technical/access issues. Two methods: 'duration' fixes the number of installments and lets the system compute the per-installment amount from the outstanding balance; 'amount' fixes the recurring installment amount and runs as long as needed. Use 'duration' for \"X monthly installments\" and 'amount' for \"<amount>/month\". Search case vouchers first to know the outstanding balance.",
    inputSchema,
    outputSchema,
    editSchema,
    summarizeProposal: (input) => input.method === 'duration'
        ? `Payment plan (${input.duration ?? '?'} × ${input.interval})`
        : `Payment plan (${input.amount ?? '?'} per ${input.interval})`,
};
//# sourceMappingURL=create-payment-plan.js.map